import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { message } from 'antd';
import archivedFormsService from '../services/archivedFormsService';
import { filterForms } from '../utils/archivedFormsUtils';

/**
 * Arşivlenmiş formlar için ana hook
 */
export const useArchivedForms = () => {
  // State
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  
  // Redux selectors
  const appToken = useSelector((state) => state.appToken.apptoken);
  const userData = useSelector((state) => state.loginData.loginData);

  /**
   * Arşivlenmiş formları yükle
   */
  const fetchArchivedForms = useCallback(async () => {
    try {
      setLoading(true);
      const allForms = await archivedFormsService.fetchArchivedForms(appToken, userData.id);
      setForms(allForms);
      setFilteredData(allForms);
    } catch (error) {
      console.error('Arşivlenmiş formlar yüklenirken hata:', error);
      message.error('Arşivlenmiş formlar yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, [appToken, userData.id]);

  /**
   * Formu geri yükle
   */
  const handleRestore = async (record) => {
    try {
      await archivedFormsService.restoreForm(record.id, appToken, userData.id);
      message.success('Form başarıyla geri yüklendi.');
      
      // Listeyi güncelle - geri yüklenen formu kaldır
      const updatedForms = forms.filter(form => form.id !== record.id);
      setForms(updatedForms);
    } catch (error) {
      console.error('Form geri yüklenirken hata:', error);
      message.error('Form geri yüklenirken bir hata oluştu.');
    }
  };

  /**
   * Formu kalıcı olarak sil
   */
  const handleDelete = async (formId) => {
    try {
      await archivedFormsService.deleteFormPermanently(formId, appToken, userData.id);
      message.success('Form kalıcı olarak silindi.');
      
      // Listeyi güncelle - silinen formu kaldır
      const updatedForms = forms.filter(form => form.id !== formId);
      setForms(updatedForms);
    } catch (error) {
      console.error('Form silinirken hata:', error);
      message.error('Form silinirken bir hata oluştu.');
    }
  };

  // İlk yükleme
  useEffect(() => {
    fetchArchivedForms();
  }, [fetchArchivedForms]);

  // Filtreleme işlemi
  useEffect(() => {
    const filtered = filterForms(forms, searchText);
    setFilteredData(filtered);
  }, [forms, searchText]);

  return {
    // State
    forms,
    loading,
    searchText,
    filteredData,
    
    // Actions
    setSearchText,
    fetchArchivedForms,
    handleRestore,
    handleDelete
  };
};

/**
 * Tablo yönetimi için hook
 */
export const useArchivedFormsTable = () => {
  /**
   * Tablo ayarları
   */
  const getTableConfig = () => ({
    rowKey: "id",
    scroll: { x: 1400 },
    pagination: {
      defaultPageSize: 10,
      showSizeChanger: true,
      showTotal: (total, range) => 
        `${range[0]}-${range[1]} / ${total} kayıt`,
    },
    locale: {
      emptyText: 'Arşivlenmiş form bulunamadı',
    }
  });

  return {
    getTableConfig
  };
};
